import React, { useState } from "react"
// import { set } from "core-js/core/dict"
import { FiDownload } from "react-icons/fi"
import { is } from "date-fns/locale"
import Dropdown from "../SampleCodeCatalog/Dropdown/Dropdown.component"
import Tile from "../Markdown/Tile"
import CopyButton from "../CopyButton"
import { getModelSettings } from "./AgentSettings"
import * as style from "./AgentConfiguration.module.scss"

// Sub-components
import RangeInput from "./RangeInput"
import Checkbox from "./Checkbox"
import TextInput from "./TextInput"

const AgentConfiguration = () => {
  // State for form values
  const [agentType, setAgentType] = useState("")
  const [selectedContentTypes, setSelectedContentTypes] = useState(["basic_text"])
  const [contentTypeModels, setContentTypeModels] = useState({
    basic_text: { value: "azure__openai__gpt_4o", label: "Azure OpenAI GPT-4o" },
  })
  const [enhancedExtractAgent, setEnhancedExtractAgent] = useState(false)
  const [activeContentType, setActiveContentType] = useState("basic_text")

  // Per-content-type configurations
  const [contentTypeConfigs, setContentTypeConfigs] = useState({
    basic_text: {
      llmParams: {
        type: "openai_params",
        temperature: 0,
        top_k: 40,
        top_p: 0.9,
        max_tokens: 8400,
        frequency_penalty: 1.5,
        presence_penalty: 1.5,
        min_temperature: 0.0,
        max_temperature: 2.0,
        min_top_p: 0.1,
        max_top_p: 1.0,
        min_freq_pen: -2.0,
        max_freq_pen: 2.0,
        min_pres_pen: -2.0,
        max_pres_pen: 2.0,
        min_top_k: 1,
        max_top_k: 50,
      },
      promptTemplate: "It is `{current_date}`, consider these travel options `{content}` and answer the `{user_question}`.",
      systemMessage: "You are a helpful travel assistant specialized in budget travel",
    },
  })

  // Agent type options
  const agentTypeOptions = [
    { value: "ai_agent_ask", label: "AI Ask" },
    { value: "ai_agent_text_gen", label: "AI Text gen" },
    { value: "ai_agent_extract", label: "AI extract free form" },
    { value: "ai_agent_extract_structured", label: "AI Extract structured" },
  ]

  // Content type tiles data
  const contentTypes = [
    { id: "basic_text", title: "Basic Text", description: "Simple text content" },
    { id: "basic_text_multi", title: "Basic Text Multi", description: "Multiple text files" },
    { id: "basic_image", title: "Basic Image", description: "Single image file" },
    { id: "basic_image_multi", title: "Basic Image Multi", description: "Multiple image files" },
    { id: "long_text", title: "Long Text", description: "Documents and PDFs" },
    { id: "long_text_multi", title: "Long Text Multi", description: "Multiple documents" },
    { id: "spreadsheet", title: "Spreadsheet", description: "Excel or CSV files" },
  ]

  // LLM models from different providers
  const llmModels = [
    // OpenAI
    { value: "openai__o3", label: "OpenAI GPT o3" },
    { value: "openai__o4_mini", label: "OpenAI GPT-4" },
    { value: "openai__gpt_4_1", label: "OpenAI GPT-4.1" },
    { value: "openai__gpt_4_1_mini", label: "OpenAI GPT-4.1 Mini" },
    { value: "openai__gpt_4o", label: "OpenAI GPT-4o" },
    { value: "openai__gpt_4o_mini", label: "OpenAI GPT-4o Mini" },

    // Azure OpenAI
    { value: "azure__openai__gpt_4_1", label: "Azure OpenAI GPT-4.1" },
    { value: "azure__openai__gpt_4_1_mini", label: "Azure OpenAI GPT-4.1 Mini" },
    { value: "azure__openai__gpt_4o", label: "Azure OpenAI GPT-4o" },
    { value: "azure__openai__gpt_4o_mini", label: "Azure OpenAI GPT-4o Mini" },
    { value: "azure__text_embedding_ada_002", label: "Azure text-embedding-ada-002" },

    // Google Gemini
    { value: "google__gemini_2_5_pro_preview", label: "Google Gemini 2.5 Pro Preview" },
    { value: "google__gemini_2_5_flash_preview", label: "Google Gemini 2.5 Flash Preview" },
    { value: "google__gemini_2_0_flash_lite_preview", label: "Google Gemini 2.0 Flash Lite" },
    { value: "google__gemini_2_0_flash_001", label: "Google Gemini 2.0 Flash" },
    { value: "google__gemini_1_5_pro_001", label: "Google Gemini 1.5 Pro 001" },
    { value: "google__gemini_1_5_flash", label: "Google Gemini 1.5 Flash" },
    { value: "google__gemini_pro", label: "Google Gemini Pro" },

    // AWS Claude & Titan
    { value: "aws__claude_4_sonnet", label: "AWS Claude 4 Sonnet" },
    { value: "aws__claude_4_opus", label: "AWS Claude 4 Opus" },
    { value: "aws__claude_3_7_sonnet", label: "AWS Claude 3.7 Sonnet" },
    { value: "aws__claude_3_5_sonnet", label: "AWS Claude 3.5 Sonnet" },
    { value: "aws__claude_3_sonnet", label: "AWS Claude 3 Sonnet" },
    { value: "aws__claude_3_haiku", label: "AWS Claude 3 Haiku" },
    { value: "aws__titan_text_lite", label: "AWS Titan Text Lite" },

    // IBM
    { value: "ibm__llama_4_scout", label: "IBM Llama 4 Scout" },
    { value: "ibm__llama_3_2_90b_vision_instruct", label: "IBM Llama 3.2 Vision Instruct" },

    // xAI
    { value: "xai__grok_3_beta", label: "xAI Grok 3 Beta" },
    { value: "xai__grok_3_mini_reasoning_beta", label: "xAI Grok 3 Mini Reasoning Beta" },

    // Meta (keeping existing)
    { value: "meta__llama_70b", label: "Meta LLaMA 70B" },

  ]

  // Helper functions to check model types
  const isOpenAIModel = (modelValue) => modelValue && (
    modelValue.includes("openai")
    || modelValue.startsWith("azure__openai")
    || modelValue.startsWith("openai__")
    || modelValue.startsWith("azure__text_embedding")
  )

  const isGoogleModel = (modelValue) => modelValue && (
    modelValue.startsWith("google__")
  )

  const isAWSModel = (modelValue) => modelValue && (
    modelValue.startsWith("aws__")
    || modelValue.startsWith("anthropic__")
  )

  const isXAIModel = (modelValue) => modelValue && (
    modelValue.startsWith("xai__")
  )

  const isIBMModel = (modelValue) => modelValue && (
    modelValue.startsWith("ibm__")
    || modelValue.startsWith("meta__")
  )

  // Get the current active content type's model
  const currentModel = contentTypeModels[activeContentType]?.value

  // Get current content type's configuration
  const getCurrentConfig = () => contentTypeConfigs[activeContentType] || {
    llmParams: {},
    promptTemplate: "",
    systemMessage: "",
  }

  // Check if agent type is extraction-related
  const isExtractionAgent = agentType.value && (
    agentType.value === "ai_agent_extract_free"
    || agentType.value === "ai_agent_extract_structured"
  )

  // Handle content type tile click
  const handleContentTypeClick = (contentTypeId) => {
    setSelectedContentTypes((prev) => {
      if (prev.includes(contentTypeId)) {
        // Remove content type and its configurations
        setContentTypeModels((models) => {
          const newModels = { ...models }
          delete newModels[contentTypeId]
          return newModels
        })
        setContentTypeConfigs((configs) => {
          const newConfigs = { ...configs }
          delete newConfigs[contentTypeId]
          return newConfigs
        })

        // Set active tab to first remaining content type
        const remaining = prev.filter((id) => id !== contentTypeId)
        if (remaining.length > 0 && activeContentType === contentTypeId) {
          setActiveContentType(remaining[0])
        }

        return remaining
      } else {
        // Add content type with default configurations
        setContentTypeModels((models) => ({
          ...models,
          [contentTypeId]: llmModels[0], // Default to first model
        }))

        // Initialize with default configuration
        setContentTypeConfigs((configs) => ({
          ...configs,
          [contentTypeId]: {
            llmParams: {
              type: "openai_params",
              temperature: 0,
              top_k: 40,
              top_p: 0.9,
              max_tokens: 8400,
              frequency_penalty: 1.5,
              presence_penalty: 1.5,
              min_temperature: 0.0,
              max_temperature: 2.0,
              min_top_p: 0.1,
              max_top_p: 1.0,
              min_freq_pen: -2.0,
              max_freq_pen: 2.0,
              min_pres_pen: -2.0,
              max_pres_pen: 2.0,
              min_top_k: 1,
              max_top_k: 50,
            },
            promptTemplate: "It is `{current_date}`, consider these travel options `{content}` and answer the `{user_question}`.",
            systemMessage: "You are a helpful travel assistant specialized in budget travel",
          },
        }))

        return [...prev, contentTypeId]
      }
    })
  }

  // Handle agentType changes
  const handleAgentTypeChange = (agentType) => {
    setAgentType(agentType)
    if (agentType.value !== "ai_agent_extract_structured"
        && agentType.value !== "ai_agent_extract_free"
    ) {
      setEnhancedExtractAgent(false)
    }
  }

  // Handle model selection for a specific content type
  const handleContentTypeModelChange = (contentTypeId, model) => {
    setContentTypeModels((prev) => ({
      ...prev,
      [contentTypeId]: model,
    }))

    // Update LLM parameters for the specific content type
    const modelSettings = getModelSettings(model.value)
    setContentTypeConfigs((prev) => ({
      ...prev,
      [contentTypeId]: {
        ...prev[contentTypeId],
        llmParams: {
          ...prev[contentTypeId]?.llmParams,
          ...modelSettings,
        },
      },
    }))
  }

  // Handle LLM parameter changes for current content type
  const handleLlmParamChange = (param, value) => {
    setContentTypeConfigs((prev) => ({
      ...prev,
      [activeContentType]: {
        ...prev[activeContentType],
        llmParams: {
          ...prev[activeContentType]?.llmParams,
          [param]: value,
        },
      },
    }))
  }

  // Handle prompt template change for current content type
  const handlePromptTemplateChange = (value) => {
    setContentTypeConfigs((prev) => ({
      ...prev,
      [activeContentType]: {
        ...prev[activeContentType],
        promptTemplate: value,
      },
    }))
  }

  // Handle system message change for current content type
  const handleSystemMessageChange = (value) => {
    setContentTypeConfigs((prev) => ({
      ...prev,
      [activeContentType]: {
        ...prev[activeContentType],
        systemMessage: value,
      },
    }))
  }

  // Download JSON configuration as file
  const downloadJson = () => {
    const config = generateJsonObject()
    const dataStr = JSON.stringify(config, null, 2)
    const dataUri = `data:application/json;charset=utf-8,${encodeURIComponent(dataStr)}`

    const exportFileDefaultName = `agent-config-${new Date().toISOString().split('T')[0]}.json`

    const linkElement = document.createElement('a')
    linkElement.setAttribute('href', dataUri)
    linkElement.setAttribute('download', exportFileDefaultName)
    linkElement.click()
  }

  // Generate JSON configuration object
  const generateJsonObject = () => {
    if (isExtractionAgent && enhancedExtractAgent) {
      return {
        type: "ai_agent_id",
        id: "enhanced_extract_agent",
      }
    }

    if (selectedContentTypes.length === 0) {
      return {
        type: agentType.value || "ai_agent_ask",
        "// Note": "Select content types to see configuration",
      }
    }

    const config = {
      type: agentType.value || "ai_agent_ask",
    }

    selectedContentTypes.forEach((contentType) => {
      const contentTypeModel = contentTypeModels[contentType]
      const modelValue = contentTypeModel?.value || "azure__openai__gpt_4o"
      const contentConfig = contentTypeConfigs[contentType]
      const llmParams = contentConfig?.llmParams || {}

      const baseConfig = {
        llm_endpoint_params: {
          type: llmParams.type || "openai_params",
          temperature: llmParams.temperature || 0,
          top_p: llmParams.top_p || 0.9,
          ...((isOpenAIModel(modelValue) || isXAIModel(modelValue)) && {
            frequency_penalty: llmParams.frequency_penalty || 1.5,
            presence_penalty: llmParams.presence_penalty || 1.5,
          }),
          ...((isOpenAIModel(modelValue) || isXAIModel(modelValue)) && llmParams.stop && {
            stop: llmParams.stop,
          }),
          ...((isGoogleModel(modelValue) || isIBMModel(modelValue)) && {
            top_k: llmParams.top_k || 40,
          }),
        },
        model: modelValue,
        num_tokens_for_completion: llmParams.max_tokens || 8400,
        prompt_template: contentConfig?.promptTemplate || "",
        system_message: contentConfig?.systemMessage || "",
      }

      // Add embeddings for long text types
      if (contentType.includes("long_text")) {
        baseConfig.embeddings = {
          model: "azure__openai__text_embedding_ada_002",
          strategy: {
            id: "basic",
            num_tokens_per_chunk: 64,
          },
        }
      }

      config[contentType] = baseConfig
    })

    return config
  }

  // Generate JSON configuration for display
  const generateJson = () => JSON.stringify(generateJsonObject(), null, 2)

  return (
    <div className={style.agentConfiguration}>
      <aside className={style.leftColumn}>
        <h2>Agent configuration</h2>
        <p className={style.description}>
          Configure your AI agent's settings and LLM fine-tune parameters.
        </p>

        {/* Agent Configuration Wrapper */}
        <div className={style.agentWrapper}>
          {/* Agent Type Selection */}
          <div className={style.section}>
            <h3>1. Choose agent type</h3>
            <Dropdown
              dropdownType="wide"
              placeHolder="Select agent type"
              options={agentTypeOptions}
              onChange={(selectedValue) => handleAgentTypeChange(selectedValue)}
              selectedValue={agentType}
            />

            {/* Enhanced Extract Agent */}
            {isExtractionAgent && (
            <div className={style.section}>
              <label>Use predefine extract agent</label>
              <Checkbox
                label="Enhanced Extract Agent"
                checked={enhancedExtractAgent}
                onChange={setEnhancedExtractAgent}
              />
            </div>
            )}
          </div>
        </div>

        {/* Content Types Wrapper */}
        {(!enhancedExtractAgent) && (
          <div className={style.contentTypesWrapper}>
            <div className={style.section}>
              <h3>2. Add content types</h3>
              <div className={style.tilesGrid}>
                {contentTypes.map((contentType) => (
                  <div
                    key={contentType.id}
                    onClick={() => handleContentTypeClick(contentType.id)}
                    className={style.tileWrapper}
                  >
                    <Tile
                      title={contentType.title}
                      active={selectedContentTypes.includes(contentType.id)}
                    >
                      <p>{contentType.description}</p>
                    </Tile>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Content Type Configurations */}
        {(selectedContentTypes.length > 0 && !enhancedExtractAgent) && (
        <div className={style.configurationWrapper}>
          <h3>3. Configure each content type object</h3>
          {/* Tab Navigation */}
          <div className={style.tabNavigation}>
            {selectedContentTypes.map((contentTypeId) => {
              const contentType = contentTypes.find((ct) => ct.id === contentTypeId)
              return (
                <button
                  key={contentTypeId}
                  className={`${style.tabButton} ${activeContentType === contentTypeId ? style.active : ''}`}
                  onClick={() => setActiveContentType(contentTypeId)}
                >
                  {contentType?.title}
                </button>
              )
            })}
          </div>

          {/* Tab Content */}
          { !enhancedExtractAgent && (
          <div className={style.tabContent}>
            {selectedContentTypes.map((contentTypeId) => {
              const contentType = contentTypes.find((ct) => ct.id === contentTypeId)
              const config = getCurrentConfig()
              const isActive = activeContentType === contentTypeId

              if (!isActive) return null

              return (
                <div key={contentTypeId} className={style.contentTypeConfig}>
                  {/* Model Selection */}
                  <div className={style.modelSection}>
                    <h4>3.1 Choose LLM model</h4>
                    <Dropdown
                      dropdownType="wide"
                      placeHolder="Select LLM model"
                      options={llmModels}
                      onChange={(selectedValue) => handleContentTypeModelChange(contentTypeId, selectedValue)}
                      selectedValue={contentTypeModels[contentTypeId] || ""}
                    />
                  </div>

                  {/* LLM Parameters */}
                  <div className={style.llmSection}>
                    <h4>3.2 Fine-tune LLM parameters</h4>
                    <div className={style.parametersGrid}>
                      <RangeInput
                        label="Temperature"
                        min={config.llmParams?.min_temperature || 0}
                        max={config.llmParams?.max_temperature || 2}
                        step={0.1}
                        value={config.llmParams?.temperature || 0}
                        onChange={(value) => handleLlmParamChange('temperature', value)}
                      />
                      {(isGoogleModel(currentModel) || isIBMModel(currentModel)) && (
                      <RangeInput
                        label="Top K"
                        min={config.llmParams?.min_top_k || 1}
                        max={config.llmParams?.max_top_k || 50}
                        step={1}
                        value={config.llmParams?.top_k || 40}
                        onChange={(value) => handleLlmParamChange('top_k', value)}
                      />
                      )}
                      <RangeInput
                        label="Top P"
                        min={config.llmParams?.min_top_p || 0.1}
                        max={config.llmParams?.max_top_p || 1.0}
                        step={0.05}
                        value={config.llmParams?.top_p || 0.9}
                        onChange={(value) => handleLlmParamChange('top_p', value)}
                      />
                      <RangeInput
                        label="Max Tokens"
                        min={100}
                        max={10000}
                        step={100}
                        value={config.llmParams?.max_tokens || 8400}
                        onChange={(value) => handleLlmParamChange('max_tokens', value)}
                      />
                      {(isOpenAIModel(currentModel) || isXAIModel(currentModel)) && (
                      <>
                        <RangeInput
                          label="Frequency Penalty"
                          min={config.llmParams?.min_freq_pen || -2}
                          max={config.llmParams?.max_freq_pen || 2}
                          step={0.1}
                          value={config.llmParams?.frequency_penalty || 1.5}
                          onChange={(value) => handleLlmParamChange('frequency_penalty', value)}
                        />
                        <RangeInput
                          label="Presence Penalty"
                          min={config.llmParams?.min_pres_pen || -2}
                          max={config.llmParams?.max_pres_pen || 2}
                          step={0.1}
                          value={config.llmParams?.presence_penalty || 1.5}
                          onChange={(value) => handleLlmParamChange('presence_penalty', value)}
                        />
                      </>
                      )}
                      {(isOpenAIModel(currentModel) || isXAIModel(currentModel)) && (
                      <TextInput
                        label="Stop Sequence"
                        value={config.llmParams?.stop || ""}
                        onChange={(value) => handleLlmParamChange('stop', value)}
                        placeholder="Enter stop sequence"
                      />
                      )}
                    </div>
                  </div>

                  {/* Prompts and Messages */}
                  <div className={style.promptSection}>
                    <h4>3.3 Customize prompts and messages</h4>
                    <TextInput
                      label="Prompt Template"
                      value={config.promptTemplate || ""}
                      onChange={handlePromptTemplateChange}
                      placeholder="Enter prompt template"
                      multiline
                      rows={4}
                    />
                    <TextInput
                      label="System Message"
                      value={config.systemMessage || ""}
                      onChange={handleSystemMessageChange}
                      placeholder="Enter system message"
                      multiline
                      rows={3}
                    />
                  </div>
                </div>
              )
            })}
          </div>
          )}
        </div>
        )}
      </aside>

      <nav className={style.rightColumn}>
        <div className={style.jsonHeader}>
          <h3>Generated configuration</h3>
          <div className={style.jsonActions}>
            <CopyButton text={generateJson()} />
            <button
              className={style.downloadButton}
              onClick={downloadJson}
              title="Download JSON configuration"
            >
              <FiDownload />
              <span>Download configuration</span>
            </button>
          </div>
        </div>
        <pre className={style.jsonOutput}>
          <code>{generateJson()}</code>
        </pre>
      </nav>
    </div>
  )
}

export default AgentConfiguration
